import {
  Box,
  Button,
  Dropdown,
  Icon,
  NewModal,
  Table,
  useRect
} from '@platform/ui-library';
import _ from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  channelTypeOptions,
  channelTypeToName
} from '../../../const';
import { PopUp } from '../../../form_components';
import { useServices } from '../../../hooks';
import {
  Design,
  DesignChannelType,
  DesignRootSettings,
  NodeType
} from '../../../types';
import styles from '@app/styles/components/modal.module.scss';

export type ManageChannelsModalProps = {
  isOpen: boolean;
  onComplete: (rootSettings: DesignRootSettings | null) => void;
  serialized: Design;
};

export const ManageChannelsModal: FC<ManageChannelsModalProps> = ({
  isOpen,
  onComplete,
  serialized
}) => {
  const { errorMessageService } = useServices();
  const [rootSettings, setRootSettings] = useState(serialized.rootSettings);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const {
    channels,
    defaultChannel
  } = rootSettings;
  const [setRef, refRect, updateRect] = useRect<HTMLButtonElement>();
  useEffect(() => {
    if (isOpen) {
      setRootSettings(_.cloneDeep(serialized.rootSettings));
    }
  }, [serialized.rootSettings, isOpen]);
  const missingChannels = useMemo(() => {
    return Object.fromEntries(
      channelTypeOptions.filter(({ value }) => {
        return !(value in channels);
      }).map(({ name, value }) => {
        return [value, name];
      })
    );
  }, [channels]) as Record<DesignChannelType, string>;
  const getDefaultToAdd = () => Object.keys(missingChannels)[0] as DesignChannelType;
  const [toAdd, setToAdd] = useState(getDefaultToAdd());
  const data = useMemo(() => {
    return channelTypeOptions.filter(({ value }) => {
      return value in channels;
    }).map(({ name, value: channel }) => {
      const isDefault = channel === defaultChannel;
      return {
        id: `${channel}-${defaultChannel}`,
        Channel: name,
        Default: (
          <input
            type="radio"
            checked={isDefault}
            onChange={() => {
              setRootSettings({
                ...rootSettings,
                defaultChannel: channel
              });
            }}
          />
        ),
        '': (
          <button
            onClick={() => {
              if (isDefault) {
                errorMessageService.addError({
                  message: 'Cannot remove the default channel.',
                  title: 'Unable to Delete'
                });
                return;
              }
              const channelName = channelTypeToName[channel];
              const usedOn = serialized.components.objects.find((obj) => {
                switch (obj._class) {
                  case NodeType.exit:
                    return obj.props.config.exitComponent?.channelType === channelName;
                  case NodeType.session:
                    return obj.props.config.channelName === channelName;
                }
              });
              if (usedOn) {
                errorMessageService.addError({
                  message: `Caonnot remove channel because it is used on "${usedOn.name}".`,
                  title: 'Unable to Delete'
                });
                return;
              }

              const channels = {
                ...rootSettings.channels
              };
              delete channels[channel];
              setRootSettings({
                ...rootSettings,
                channels
              });
            }}
          >
            <Icon icon="delete" />
          </button>
        )
      };
    });
  }, [channels, defaultChannel]);

  const openAddForm = () => {
    if (!Object.keys(missingChannels).length) {
      errorMessageService.addError({
        message: 'No more channels to add.',
        title: 'Unable to Add'
      });
      return;
    }
    updateRect();
    setToAdd(getDefaultToAdd());
    setIsAddOpen(true);
  };

  const addChannel = () => {
    setIsAddOpen(false);
    setRootSettings({
      ...rootSettings,
      channels: {
        ...rootSettings.channels,
        [toAdd]: {}
      }
    });
  };

  return (
    <NewModal isOpen={isOpen}>
      <Box className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h3>Channels</h3>
            <button ref={setRef} type='button' onClick={openAddForm}>
              <Icon icon="plus" />
              <span>Add Channel</span>
            </button>

            {(!isAddOpen) ? null : (
              <PopUp
                handleClose={() => {
                  setIsAddOpen(false);
                }}
                refRect={refRect}
              >
                <Dropdown
                  options={missingChannels}
                  onChange={(channel) => {
                    setToAdd(channel as DesignChannelType);
                  }}
                  value={toAdd}
                />
                <div className="flex-end pad-top">
                  <Button
                    onClick={addChannel}
                    theme="outlineHighlight"
                  >
                    Add
                  </Button>
                </div>
              </PopUp>
            )}

          </div>

          <div className={styles.tableWrapper}>
            <p className="p-icon">
              <Icon icon="info" /> The selected channel is the default in the canvas, when the application opens, and
              the fallback when a text is empty.
            </p>
            <Table data={data} />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            onClick={() => {
              onComplete(null);
            }}
            theme="outline"
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onComplete(rootSettings);
            }}
          >
            Save
          </Button>
        </div>
      </Box>
    </NewModal>
  );
};
